import React, { useEffect } from "react";
import { ButtonApolloSecondary } from "../../../Button/Button";
import ProductWidgetContainer from "./ProductWidgetContainer";
import styled from "styled-components/macro";
import {
  getNumberInputValue,
  getNumberOrUndefinedFromString,
} from "../../../../utils/inputUtils";
import InputTextApollo from "../../../inputs/InputTextApollo";
import { Autocomplete } from "@material-ui/lab";
import { InputLabel, FormControl, TextField, Icon } from "@material-ui/core";
import { v4 as uuidv4 } from "uuid";

const JobsContainer = styled.div`
  margin-top: 20px;
`;

const InputContainer = styled.div`
  margin-bottom: 20px;
`;

const JobBasedProductWidget = ({
  product,
  onRemove,
  onProductChange,
  defaultConcurrentUsers,
  showDetails,
  canBeRemoved,
}: {
  product: ProductOptionInterface;
  onRemove: () => void;
  onProductChange: (updatedProduct: ProductOptionInterface) => void;
  defaultConcurrentUsers?: number;
  showDetails: boolean;
  canBeRemoved: 0 | 1;
}) => {
  const canAddJob =
    product?.properties?.jobs?.length !==
    product?.properties?.jobOptions?.length;

  const currentlySelectedMap: any = (product?.properties?.jobs || []).reduce(
    (accum: any, option: any) => {
      accum[option.name] = true;
      return accum;
    },
    {}
  );

  useEffect(() => {
    if (typeof defaultConcurrentUsers === "number") {
      const productCopy = { ...product };
      productCopy.properties = { ...productCopy.properties };
      productCopy.properties.concurrentUsers = defaultConcurrentUsers;
      onProductChange(productCopy);
    }
    // Only want this on mount
    // eslint-disable-next-line
  }, []);

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e?.currentTarget || {};
    if (product?.properties && name) {
      const productCopy = { ...product };
      productCopy.properties = { ...productCopy.properties };
      productCopy.properties[name] = getNumberOrUndefinedFromString(value);
      onProductChange(productCopy);
    }
  };

  const addJob = () => {
    if (product?.properties?.jobs && onProductChange) {
      const productCopy = { ...product };
      productCopy.properties = { ...productCopy.properties };
      productCopy.properties.jobs = [...productCopy.properties.jobs];
      productCopy.properties.jobs.push({
        key: uuidv4(),
      });
      onProductChange(productCopy);
    }
  };

  const removeJob = (job: any) => {
    if (product?.properties?.jobs && onProductChange) {
      const productCopy = { ...product };
      productCopy.properties = { ...productCopy.properties };
      productCopy.properties.jobs = productCopy.properties.jobs.filter(
        (j: any) => j.key !== job.key
      );
      onProductChange(productCopy);
    }
  };

  const handleJobOptionChange = (e: any, value: any) => {
    if (value) {
      const productCopy = { ...product };
      productCopy.properties = { ...productCopy.properties };
      productCopy.properties.jobs = productCopy.properties.jobs.map((j: any) =>
        j.key !== value.key ? j : value
      );
      onProductChange(productCopy);
    }
  };

  const renderJobs = () => {
    return (product?.properties?.jobs || []).map((job: any) => {
      return (
        <ProductWidgetContainer
          key={job.key}
          title={
            <FormControl variant="outlined">
              <InputLabel className="select-label" htmlFor="test-dropdown">
                Select Job
              </InputLabel>
              <Autocomplete
                value={job}
                options={(product?.properties?.jobOptions || []).map(
                  (opt: any) => ({
                    name: opt.name,
                    value: opt.name,
                    key: job.key,
                  })
                )}
                getOptionDisabled={(option: any) =>
                  currentlySelectedMap[option.name]
                }
                getOptionLabel={(option: any) => option.name}
                disableClearable
                style={{
                  width: 300,
                }}
                onChange={handleJobOptionChange}
                popupIcon={<Icon>expand_more</Icon>}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    placeholder="Type to search..."
                  />
                )}
              />
            </FormControl>
          }
          onRemove={() => removeJob(job)}
          showDetails={showDetails}
        ></ProductWidgetContainer>
      );
    });
  };

  return (
    <ProductWidgetContainer
      onRemove={onRemove}
      title={product.name}
      canBeRemoved={canBeRemoved}
      showDetails={showDetails}
    >
      <InputContainer>
        <InputTextApollo
          label="Concurrent Users"
          variant="outlined"
          inputProps={{ type: "number" }}
          InputProps={{
            name: "concurrentUsers",
            value: getNumberInputValue(
              product?.properties?.concurrentUsers || ""
            ),
            onChange: handleNumberChange,
          }}
        />
      </InputContainer>
      <InputContainer>
        <InputTextApollo
          label="Number of Skills"
          variant="outlined"
          inputProps={{ type: "number" }}
          InputProps={{
            name: "numberOfSkills",
            value: getNumberInputValue(
              product?.properties?.numberOfSkills || ""
            ),
            onChange: handleNumberChange,
          }}
        />
      </InputContainer>
      <ButtonApolloSecondary onClick={addJob} disabled={!canAddJob}>
        Add Job
      </ButtonApolloSecondary>
      <JobsContainer>{renderJobs()}</JobsContainer>
    </ProductWidgetContainer>
  );
};

export default JobBasedProductWidget;
